import {
    PayoutBatchFilters,
    PayoutBatchItem,
    PayoutRecipientKind,
    previewPayoutBatchItems,
} from '@/lib/api/financial/payout-batch-api'
import { DetailType, useDetailPopup } from '@/components/detail-popup'
import { copyToClipboard } from '@/lib/utils/clipboard'
import { useEffect, useState } from 'react'
import { MdContentCopy, MdSearch, MdSwapHoriz } from 'react-icons/md'
import { toast } from 'react-toastify'
import { formatCurrency } from '../config/report-configs'

interface PayoutBatchItemsPickerProps {
    filters: PayoutBatchFilters
    recipient: { kind: PayoutRecipientKind; entityId: number }
    /**
     * Como ler `marked`: no modo `exclude` são os lançamentos que **saem**; no modo
     * `include` (depois de "Inverter"), os únicos que **entram**.
     */
    mode: 'exclude' | 'include'
    /**
     * Os lançamentos marcados, com o valor de cada um.
     *
     * É um `Map` e não um `Set` porque o rodapé precisa somar/subtrair valores sem ter
     * a lista inteira carregada — o total do destinatário vem agregado do banco, e o
     * que a tela conhece é só a página aberta.
     */
    marked: Map<number, number>
    onToggle: (item: PayoutBatchItem) => void
    /** Troca o sentido da seleção e zera as marcações. */
    onInvert: () => void
}

const PAGE_SIZE = 20

/** Sufixo que o backend grava na descrição do repasse: `(#exam 72976)`. */
const SOURCE_REF_PATTERN = /\s*\(#([a-z_]+) (\d+)\)\s*$/i

/**
 * Origem gravada no sufixo (`sourceType` em minúsculas) -> detalhe que o popup sabe
 * abrir. Teleconsulta não tem página de detalhe no popup e fica sem clique.
 */
const SOURCE_DETAIL_TYPE: Record<string, DetailType> = {
    exam: 'exam',
    consultation: 'consultation',
    procedure: 'order',
}

/**
 * Separa o código do atendimento de origem do resto da descrição.
 *
 * O código vem no fim do texto e era o primeiro a sumir no `truncate` — justamente o
 * que o operador usa para achar o atendimento. Puxado para a frente, fica sempre
 * visível, e o texto que sobra pode ser cortado sem perder nada que identifique.
 */
function splitSourceRef(description: string | null): {
    code: string | null
    detailType: DetailType | null
    text: string
} {
    if (!description) return { code: null, detailType: null, text: '' }
    const match = description.match(SOURCE_REF_PATTERN)
    if (!match) return { code: null, detailType: null, text: description }
    return {
        code: match[2],
        detailType: SOURCE_DETAIL_TYPE[match[1].toLowerCase()] ?? null,
        text: description.slice(0, match.index).trim(),
    }
}

async function copyCode(code: string) {
    if (await copyToClipboard(code)) {
        toast.success(`Código ${code} copiado.`)
    } else {
        toast.error('Não foi possível copiar o código.')
    }
}

/**
 * Composição de um destinatário, paginada, para o operador escolher o que entra.
 *
 * **Paginada de verdade, no banco.** A lista chegava inteira e era renderizada de uma
 * vez: com centenas de repasses o navegador engasgava, e achar aquele paciente para
 * desmarcar virava rolagem no escuro. Daí também a busca — por número de lançamento,
 * texto da descrição **ou nome do paciente** —, porque quem escolhe já sabe o que
 * procura, e é pelo paciente que ele reconhece o atendimento (o mesmo parceiro repete
 * o mesmo serviço dezenas de vezes no período).
 *
 * **Os dois sentidos da seleção.** O padrão é "vem tudo marcado, desmarque o que não
 * entra" — certo quando são poucas exceções. Quando é o contrário (pagar só um punhado
 * de uma lista longa), desmarcar dezenas é onde se erra e se paga o que não devia: o
 * botão **Inverter** vira a lógica, tudo fica desmarcado e ele marca os poucos.
 */
export default function PayoutBatchItemsPicker({
    filters,
    recipient,
    mode,
    marked,
    onToggle,
    onInvert,
}: PayoutBatchItemsPickerProps) {
    const [items, setItems] = useState<PayoutBatchItem[]>([])
    const [page, setPage] = useState(1)
    const [pageCount, setPageCount] = useState(1)
    const [total, setTotal] = useState(0)
    const [search, setSearch] = useState('')
    const [term, setTerm] = useState('')
    const [loading, setLoading] = useState(false)
    const { openDetail } = useDetailPopup()

    // Busca só depois que o operador para de digitar: cada tecla seria uma consulta.
    useEffect(() => {
        const timer = setTimeout(() => {
            setTerm(search)
            setPage(1)
        }, 400)
        return () => clearTimeout(timer)
    }, [search])

    useEffect(() => {
        let active = true
        setLoading(true)
        previewPayoutBatchItems(filters, recipient, {
            page,
            pageSize: PAGE_SIZE,
            search: term || undefined,
        })
            .then(res => {
                if (!active) return
                const result = res.result
                setItems(result?.items ?? [])
                setTotal(result?.total ?? 0)
                setPageCount(result?.pageCount ?? 1)
            })
            .catch(() => {
                if (!active) return
                setItems([])
                setTotal(0)
                setPageCount(1)
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
        // `filters` é um objeto novo a cada render do pai; comparar serializado evita
        // um laço de requisições.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filters), recipient.kind, recipient.entityId, page, term])

    const isInverted = mode === 'include'

    return (
        <div className="border-t border-base-300">
            <div className="p-2 flex flex-wrap items-center gap-2">
                <label className="input input-bordered input-xs flex items-center gap-2 flex-1 min-w-[14rem]">
                    <MdSearch className="text-base-content/50" />
                    <input
                        type="text"
                        className="grow"
                        placeholder="Buscar por paciente, nº do lançamento ou descrição"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </label>
                <button
                    type="button"
                    className={`btn btn-xs gap-1 ${isInverted ? 'btn-secondary' : 'btn-ghost'}`}
                    onClick={onInvert}
                    title={
                        isInverted
                            ? 'Voltar ao padrão: todos entram, você desmarca as exceções'
                            : 'Inverter: nenhum entra, você marca os que vão ser pagos'
                    }
                >
                    <MdSwapHoriz className="text-sm" />
                    {isInverted ? 'Marcando os que entram' : 'Inverter seleção'}
                </button>
                <span className="text-xs text-base-content/60 whitespace-nowrap">
                    {total} lançamento{total === 1 ? '' : 's'}
                </span>
            </div>

            {/* A busca filtra a lista, não a seleção: marcar tudo o que apareceu e
                limpar o campo poderia dar a impressão de que só aquilo entra. No modo
                invertido isso é ainda mais fácil de confundir, então o aviso fica. */}
            {isInverted ? (
                <div className="px-2 pb-2 text-xs text-secondary">
                    Seleção invertida: só os lançamentos marcados entram nesta
                    solicitação.
                </div>
            ) : null}

            <div className="overflow-x-auto">
                <table className="table table-xs">
                    <thead>
                        <tr>
                            <th
                                className="w-8"
                                title={
                                    isInverted
                                        ? 'Marque os que entram nesta solicitação'
                                        : 'Desmarque o que não entra nesta solicitação'
                                }
                            >
                                Incl.
                            </th>
                            <th>Lanç.</th>
                            <th>Paciente</th>
                            <th>Vencimento</th>
                            <th>Descrição</th>
                            <th className="text-right">Repasse</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="text-center py-4">
                                    <span className="loading loading-spinner loading-sm" />
                                </td>
                            </tr>
                        ) : items.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={6}
                                    className="text-center py-4 text-base-content/60"
                                >
                                    Nenhum lançamento encontrado.
                                </td>
                            </tr>
                        ) : (
                            items.map(item => {
                                const isMarked = marked.has(item.billingId)
                                // No modo invertido a marca É a inclusão; no padrão, a
                                // marca é a exclusão.
                                const isIn = isInverted ? isMarked : !isMarked
                                const { code, detailType, text } = splitSourceRef(
                                    item.description
                                )
                                return (
                                    <tr
                                        key={item.billingId}
                                        className={isIn ? '' : 'opacity-40'}
                                    >
                                        <td>
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-xs"
                                                checked={isIn}
                                                onChange={() => onToggle(item)}
                                                aria-label={`Incluir o lançamento ${item.billingId}`}
                                            />
                                        </td>
                                        <td>#{item.billingId}</td>
                                        {/* O paciente é por quem o operador reconhece o
                                            atendimento que ainda não foi feito — sem ele,
                                            desmarcar é escolher por número. */}
                                        <td className="max-w-[9rem] truncate">
                                            {item.clientName || '—'}
                                        </td>
                                        <td>
                                            {item.dueDate
                                                ? new Date(
                                                      item.dueDate
                                                  ).toLocaleDateString('pt-BR')
                                                : '—'}
                                        </td>
                                        <td className="max-w-xs">
                                            <div className="flex items-center gap-1.5 min-w-0">
                                                {code ? (
                                                    <button
                                                        type="button"
                                                        className="badge badge-ghost badge-sm gap-1 shrink-0 font-mono cursor-pointer hover:badge-primary"
                                                        onClick={() => copyCode(code)}
                                                        title={`Copiar o código ${code}`}
                                                    >
                                                        #{code}
                                                        <MdContentCopy className="text-[0.7rem]" />
                                                    </button>
                                                ) : null}
                                                {/* Abre o atendimento no popup, sem sair do
                                                    modal: é para conferir o que se está pagando. */}
                                                {code && detailType ? (
                                                    <button
                                                        type="button"
                                                        className="link link-hover truncate text-left min-w-0"
                                                        onClick={() =>
                                                            openDetail(detailType, Number(code))
                                                        }
                                                        title={`${item.description}\n\nClique para abrir o atendimento`}
                                                    >
                                                        {text || 'Abrir atendimento'}
                                                    </button>
                                                ) : (
                                                    <span
                                                        className="truncate"
                                                        title={item.description || undefined}
                                                    >
                                                        {text || (code ? '' : '—')}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-right">
                                            {formatCurrency(item.amount)}
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {pageCount > 1 ? (
                <div className="flex items-center justify-between gap-2 p-2">
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        disabled={page <= 1 || loading}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                        Anterior
                    </button>
                    <span className="text-xs text-base-content/60">
                        Página {page} de {pageCount}
                    </span>
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        disabled={page >= pageCount || loading}
                        onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                    >
                        Próxima
                    </button>
                </div>
            ) : null}
        </div>
    )
}
